import axios from "./_axios";
import { API } from "./apiEndPoints";
import { normalizeCloudflareStreamVideoUid } from "@/utils/simulatedCloudflareStreamWebhook";

export const getListofRoles = (account_id, page, size) => {
  try {
    let result = axios()({
      method: "get",
      url: `${API.ACCOUNTList}${account_id}/role/`,
      data: { page: page, size: size },
    });
    return result;
  } catch (e) {
    console.error(e);
  }
};

export const createRole = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/role/`,
    data: data,
  });
  return result;
};
export const createRoleV2 = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/`,
    data: data,
  });
  return result;
};

export const deleteRole = (account_id, res_id) => {
  let result = axios()({
    method: "delete",
    url: `${API.ACCOUNTList}${account_id}/role/${res_id}/`,
  });
  return result;
};

export const roleStateUpdate = (account_id, res_id, data) => {
  let result = axios()({
    method: "put",
    url: `${API.ACCOUNTList}${account_id}/role/${res_id}/`,
    data: data,
  });
  return result;
};

export const failCheckPointEvaluation = (account_id, res_id, chkp_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/${chkp_id}/fail/`,
    data: data,
  });
  return result;
};

export const passCheckPointEvaluation = (account_id, res_id, chkp_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/${chkp_id}/pass/`,
    data: data,
  });
  return result;
};

export const notApplicableCheckPointEvaluation = (account_id, res_id, chkp_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/${chkp_id}/not-applicable/`,
    data: data,
  });
  return result;
};

export const resetCheckPointEvaluation = (account_id, res_id, chkp_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/${chkp_id}/reset/`,
    data: data,
  });
  return result;
};

/**
 * Rule 11 standalone bulk reset — resets every inspection row on a checkpoint
 * (all versions) in one transaction. Mirrors the per-row reset endpoint shape;
 * data is `{ reason, access_id }` (BULK_RESET_REASON_REQUIRED / authorization
 * pre-checks live on the backend — ER-11302). ER-11307.
 */
export const bulkResetCheckpointInspections = (account_id, res_id, chkp_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/${chkp_id}/bulk-reset/`,
    data: data,
  });
  return result;
};

/**
 * Checkpoint Inspections tab list — every inspection on one checkpoint, grouped
 * by version (Reset bucket first, then newest / current). ER-11594 / MDP-592.
 * Repeatable filter params serialize as status=passed&status=failed.
 */
export const getCheckpointInspectionsByVersion = (
  accountId,
  responsibilityId,
  checkpointId,
  params = {}
) => {
  return axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${accountId}/responsibility/${responsibilityId}/checkpoint/${checkpointId}/inspections-by-version/`,
    params,
    paramsSerializer: { indexes: null },
  });
};

/**
 * Records or changes one protocol step result on the employee's inspection
 * (Rule 14, MDP-648). `data` is `{ employee, step_sequence, result: "passed"|"failed", comment }`.
 */
export const recordCheckpointInspectionStep = (account_id, res_id, chkp_id, data) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/${chkp_id}/inspection-step/`,
    data,
  });
};
export const getProficiencyAccountBadgesByEmployee = (account_id, employee_id, limit, offset) => {
  const params = { employee: employee_id };
  if (limit != null) params.limit = limit;
  if (offset != null) params.offset = offset;
  return axios()({
    method: "get",
    url: `${API.PROFICIENCY_V2}${account_id}/badge/`,
    params,
  });
};

export const requestInspection = (account_id, responsibility_id, data) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/inspection/`,
    data,
  });
};

export const cancelInspectionRequest = (account_id, responsibility_id, evaluation_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/evaluations/${evaluation_id}/cancel/`,
  });
};

// ER-12321 — Rule 22: retry a failed request. Backend endpoint owned by ER-12319.
export const retryInspectionRequest = (account_id, responsibility_id, evaluation_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/evaluations/${evaluation_id}/retry/`,
  });
};

// ER-12321 — Rule 12: dismiss a declined or failed request. Backend endpoint owned by ER-12319.
export const dismissInspectionRequest = (account_id, responsibility_id, evaluation_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/evaluations/${evaluation_id}/dismiss/`,
  });
};

// ER-11509 — closed Inspection Requests assigned to the viewing coach (feed owned by ER-11504).
export const getCoachInspectionRequestHistory = (account_id, params = {}) => {
  return axios()({
    method: "get",
    url: `${API.PROFICIENCY_V2}${account_id}/${API.INSPECTION_REQUESTS_AS_COACH_HISTORY}`,
    params,
  });
};

export const generateVideoUploadUrl = (account_id, checkpoint_id, file_size) => {
  return axios()({
    method: "post",
    url: `${API.PROFICIENCY_V2}${account_id}/checkpoint/${checkpoint_id}/generate-video-upload-url/`,
    data: { file_size },
  });
};

export const confirmProposedVideo = (account_id, checkpoint_id, formData) => {
  return axios()({
    method: "post",
    url: `${API.PROFICIENCY_V2}${account_id}/checkpoint/${checkpoint_id}/confirm-proposed-video/`,
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * Tasked-upload context validation (idea-MDP-575). Backend owns the IQ call
 * and completes the pending upload task on a match. Timeout is 6 minutes
 * because IQ analysis can take up to 5 minutes server-side.
 */
export const videoToComponents = (
  account_id,
  responsibility_id,
  checkpoint_id,
  data,
  config = {}
) => {
  return axios()({
    method: "post",
    url: `${API.PROFICIENCY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/video-to-components/`,
    data,
    timeout: 6 * 60 * 1000,
    ...config,
  });
};

/**
 * Finish a tasked video upload after the client already received PASS from IQ
 * `/video_to_components/`. Does not call IQ.
 */
export const completeMatchedUploadTask = (account_id, responsibility_id, checkpoint_id, data) => {
  return axios()({
    method: "post",
    url: `${API.PROFICIENCY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/complete-matched-upload-task/`,
    data,
  });
};

export const getCloudflareVideoStatus = (video_id) => {
  const videoId = normalizeCloudflareStreamVideoUid(video_id);
  if (!videoId) return Promise.resolve(null);
  return axios()({
    method: "get",
    url: `${API.COMMON_V2}cloudflare-video-status/`,
    params: { video_id: videoId },
  });
};
